import { motion } from "framer-motion";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { DURATION, EASE_OUT_SOFT } from "../../lib/motion";
import { Spinner } from "./Spinner";

/**
 * Beyond Sure — Button primitive.
 *
 * Variants follow the locked brand hierarchy: BLACK is the primary action,
 * not green. Green is reserved for identity (logo, links, accents). This is
 * the load-bearing distinction that keeps the screen reading as BeyondSure
 * rather than as a generic fintech.
 *
 *   primary    : Ink surface, Paper text + arrow. 21:1 contrast, decisive.
 *   secondary  : Paper surface, Ink border + text. The Google sibling.
 *   ghost      : transparent surface, brand-green text. Used inside the
 *                Deep-Forest desktop panel for "Already have an account?"
 *                style tertiary actions.
 *
 * States covered: default, hover, focus-visible, active (tap), disabled,
 * loading.
 */
type Variant = "primary" | "secondary" | "ghost";

// Motion overrides several native HTML event handlers with its own
// callback signatures. Omit them so React + Motion type-check cleanly.
type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | "onAnimationStart"
  | "onAnimationEnd"
  | "onAnimationIteration"
  | "onDrag"
  | "onDragStart"
  | "onDragEnd"
>;

interface ButtonProps extends NativeButtonProps {
  variant?: Variant;
  loading?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
}

const BASE =
  "inline-flex items-center justify-center gap-2 select-none " +
  "font-sans text-[15px] font-semibold tracking-[-0.005em] " +
  "rounded-[var(--radius-md)] px-5 h-[52px] " +
  // Focus ring uses the brand green, not blue. Always visible on keyboard focus.
  "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
  "focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-[var(--color-paper)] " +
  "disabled:cursor-not-allowed disabled:opacity-40";

const VARIANT: Record<Variant, string> = {
  // Primary: BLACK fill, white text. This is the brand's primary action — a
  // single decisive button, never blue. Subtle inner highlight keeps it from
  // looking flat at large sizes.
  primary:
    "bg-[var(--color-ink)] text-[var(--color-paper)] " +
    "shadow-[inset_0_1px_0_rgb(255_255_255_/_0.12),0_1px_2px_rgb(0_0_0_/_0.12)] " +
    "hover:bg-[#1a1a1a] active:brightness-[0.92]",
  // Secondary: outlined, white surface, black border. Holds the Google CTA.
  secondary:
    "bg-[var(--color-paper)] text-[var(--color-ink)] " +
    "border border-[var(--color-ink)] " +
    "hover:bg-[var(--color-ink-04)] active:bg-[var(--color-ink-08)]",
  // Ghost: transparent, brand-green text. Reserved for tertiary actions
  // (e.g. "Use a different email" inside the sent-confirmation panel).
  ghost:
    "bg-transparent text-[var(--color-brand)] " +
    "hover:bg-[var(--color-brand-soft)] active:brightness-[0.95]",
};

export function Button({
  variant = "primary",
  loading = false,
  leading,
  trailing,
  fullWidth = false,
  disabled,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <motion.button
      whileHover={isDisabled ? undefined : { y: -1 }}
      whileTap={isDisabled ? undefined : { scale: 0.985 }}
      transition={{ duration: DURATION.fast, ease: EASE_OUT_SOFT }}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        BASE,
        VARIANT[variant],
        fullWidth ? "w-full" : "",
        className,
      ].join(" ")}
      {...rest}
    >
      {loading ? (
        <>
          <Spinner />
          <span>Sending…</span>
        </>
      ) : (
        <>
          {leading ? <span className="inline-flex">{leading}</span> : null}
          <span>{children}</span>
          {trailing ? <span className="inline-flex">{trailing}</span> : null}
        </>
      )}
    </motion.button>
  );
}

export default Button;
